import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/Home.css";

// Import images
import heroPhone from "../assets/hero-phone.jpeg";
import featureConversations from "../assets/feature-conversations.jpeg";
import featureRoleplay from "../assets/feature-roleplay.jpeg";
import featureFlashcards from "../assets/feature-flashcards.jpeg";
import featureTeacher from "../assets/feature-teacher.jpeg";

function Home() {
  const observerRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Intersection Observer for scroll animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const animatedElements = document.querySelectorAll(".animate-on-scroll");
    animatedElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const languages = [
    { flag: "🇬🇧", nameKey: "languages.english" },
    { flag: "🇩🇪", nameKey: "languages.german" },
    { flag: "🇮🇹", nameKey: "languages.italian" },
    { flag: "🇫🇷", nameKey: "languages.french" },
    { flag: "🇪🇸", nameKey: "languages.spanish" },
    { flag: "🇵🇹", nameKey: "languages.portuguese" },
  ];

  const features = [
    {
      id: "conversations",
      icon: t("features.conversations.icon"),
      title: t("features.conversations.title"),
      shortDesc: t("features.conversations.shortDesc"),
      fullDesc: t("features.conversations.fullDesc"),
      highlight: t("features.conversations.highlight"),
      image: featureConversations,
      imageAlt: "Immersive Conversations Feature",
    },
    {
      id: "roleplay",
      icon: t("features.roleplay.icon"),
      title: t("features.roleplay.title"),
      shortDesc: t("features.roleplay.shortDesc"),
      fullDesc: t("features.roleplay.fullDesc"),
      highlight: t("features.roleplay.highlight"),
      image: featureRoleplay,
      imageAlt: "Role-Playing Scenarios Feature",
    },
    {
      id: "flashcards",
      icon: t("features.flashcards.icon"),
      title: t("features.flashcards.title"),
      shortDesc: t("features.flashcards.shortDesc"),
      fullDesc: t("features.flashcards.fullDesc"),
      highlight: t("features.flashcards.highlight"),
      image: featureFlashcards,
      imageAlt: "Flashcard Challenges Feature",
    },
    {
      id: "teacher",
      icon: t("features.teacher.icon"),
      title: t("features.teacher.title"),
      shortDesc: t("features.teacher.shortDesc"),
      fullDesc: t("features.teacher.fullDesc"),
      highlight: t("features.teacher.highlight"),
      image: featureTeacher,
      imageAlt: "Teacher Method Feature",
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-background">
          <div className="hero-gradient-orb hero-orb-1"></div>
          <div className="hero-gradient-orb hero-orb-2"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              {t("hero.title")}
              <span className="hero-title-highlight">{t("hero.titleHighlight")}</span>
            </h1>
            <p className="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            
            <div className="hero-cta-group">
              <a
                href="https://apps.apple.com/pl/app/dialecto/id6746904195"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary"
              >
                <span className="cta-icon">📱</span>
                {t("hero.downloadAppStore")}
              </a>
              <div className="cta-secondary">
                <span className="coming-soon-badge">{t("hero.androidComingSoon")}</span>
              </div>
            </div>

            <div className="hero-languages">
              <p className="languages-label">{t("hero.availableLanguages")}</p>
              <div className="language-flags">
                {languages.map((lang, index) => (
                  <div key={index} className="language-flag" title={t(lang.nameKey)}>
                    <span className="flag-emoji">{lang.flag}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="phone-mockup">
              <div className="phone-frame">
                <div className="phone-notch"></div>
                <img
                  src={heroPhone}
                  alt="Dialecto App"
                  className="phone-screen"
                />
              </div>
              <div className="phone-glow"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="features-overview" id="features">
        <div className="section-header animate-on-scroll">
          <span className="section-badge">{t("features.sectionBadge")}</span>
          <h2 className="section-title">{t("features.sectionTitle")}</h2>
          <p className="section-subtitle">
            {t("features.sectionSubtitle")}
          </p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="feature-card animate-on-scroll"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.shortDesc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Detailed Feature Sections */}
      {features.map((feature, index) => (
        <section
          key={feature.id}
          className={`feature-detail ${index % 2 === 1 ? "reverse" : ""}`}
          id={feature.id}
        >
          <div className="feature-detail-content animate-on-scroll">
            <div className="feature-detail-text">
              <span className="feature-badge">{feature.icon} {feature.title}</span>
              <h2 className="feature-detail-title">{feature.title}</h2>
              <p className="feature-detail-desc">{feature.fullDesc}</p>
              <div className="feature-highlight">
                <span className="highlight-icon">✨</span>
                <p>{feature.highlight}</p>
              </div>
            </div>

            <div className="feature-detail-visual">
              <div className="feature-phone-mockup">
                <div className="feature-phone-frame">
                  <div className="phone-notch"></div>
                  <img
                    src={feature.image}
                    alt={feature.imageAlt}
                    className="feature-phone-screen"
                  />
                </div>
                <div className="feature-phone-glow"></div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Pricing Section */}
      <section className="pricing-section animate-on-scroll" id="pricing">
        <div className="section-header">
          <span className="section-badge">{t("pricing.sectionBadge")}</span>
          <h2 className="section-title">{t("pricing.sectionTitle")}</h2>
          <p className="section-subtitle">
            {t("pricing.sectionSubtitle")}
          </p>
        </div>

        <div className="pricing-cards">
          {/* Free Plan */}
          <div className="pricing-card free">
            <div className="pricing-card-header">
              <h3 className="plan-name">{t("pricing.free.name")}</h3>
              <div className="plan-price">
                <span className="price-amount">{t("pricing.free.price")}</span>
                <span className="price-period">{t("pricing.free.period")}</span>
              </div>
              <p className="plan-desc">{t("pricing.free.desc")}</p>
            </div>
            <ul className="plan-features">
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.free.features.basicAI")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.free.features.flashcards")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.free.features.roleplay")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.free.features.dailyLimits")}
              </li>
              <li className="feature-excluded">
                <span className="feature-icon">✕</span>
                {t("pricing.free.excluded.advancedAI")}
              </li>
              <li className="feature-excluded">
                <span className="feature-icon">✕</span>
                {t("pricing.free.excluded.adFree")}
              </li>
            </ul>
            <a
              href="https://apps.apple.com/pl/app/dialecto/id6746904195"
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-cta secondary"
            >
              {t("pricing.free.cta")}
            </a>
          </div>

          {/* Premium Plan */}
          <div className="pricing-card premium">
            <div className="popular-badge">{t("pricing.premium.badge")}</div>
            <div className="pricing-card-header">
              <h3 className="plan-name">{t("pricing.premium.name")}</h3>
              <div className="plan-price">
                <span className="price-amount">{t("pricing.premium.price")}</span>
                <span className="price-period">{t("pricing.premium.period")}</span>
              </div>
              <p className="plan-desc">{t("pricing.premium.desc")}</p>
            </div>
            <ul className="plan-features">
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                <strong>{t("pricing.premium.features.trial")}</strong>
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.premium.features.advancedAI")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.premium.features.unlimited")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.premium.features.allModes")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.premium.features.adFree")}
              </li>
              <li className="feature-included">
                <span className="feature-icon">✓</span>
                {t("pricing.premium.features.support")}
              </li>
            </ul>
            <a
              href="https://apps.apple.com/pl/app/dialecto/id6746904195"
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-cta primary"
            >
              {t("pricing.premium.cta")}
            </a>
          </div>
        </div>

        <p className="pricing-note">
          {t("pricing.note")}
        </p>
      </section>

      {/* How It Works */}
      <section className="how-it-works animate-on-scroll">
        <div className="section-header">
          <span className="section-badge">{t("howItWorks.sectionBadge")}</span>
          <h2 className="section-title">{t("howItWorks.sectionTitle")}</h2>
        </div>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>{t("howItWorks.step1.title")}</h3>
            <p>{t("howItWorks.step1.desc")}</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>{t("howItWorks.step2.title")}</h3>
            <p>{t("howItWorks.step2.desc")}</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>{t("howItWorks.step3.title")}</h3>
            <p>{t("howItWorks.step3.desc")}</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section animate-on-scroll" id="contact">
        <div className="contact-card">
          <h2>{t("contact.title")}</h2>
          <p>
            {t("contact.subtitle")}
          </p>
          <a href="mailto:support@dialectoapp.com" className="contact-link">
            <span>📧</span> support@dialectoapp.com
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section animate-on-scroll" id="faq">
        <div className="section-header">
          <span className="section-badge">{t("faq.sectionBadge")}</span>
          <h2 className="section-title">{t("faq.sectionTitle")}</h2>
          <p className="section-subtitle">{t("faq.sectionSubtitle")}</p>
        </div>

        <div className="faq-container">
          {[1, 2, 3, 4, 5].map((num) => (
            <FAQItem
              key={num}
              question={t(`faq.questions.q${num}.question`)}
              answer={t(`faq.questions.q${num}.answer`)}
            />
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta animate-on-scroll">
        <div className="final-cta-content">
          <h2>{t("finalCta.title")}</h2>
          <p>
            {t("finalCta.subtitle")}
          </p>
          <a
            href="https://apps.apple.com/pl/app/dialecto/id6746904195"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button primary large"
          >
            <span className="cta-icon">📱</span>
            {t("finalCta.button")}
          </a>
          <p className="cta-note">{t("finalCta.note")}</p>
        </div>
      </section>
    </div>
  );
}

// FAQ Item Component with accordion functionality
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? "open" : ""}`}>
      <button
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? "−" : "+"}</span>
      </button>
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    </div>
  );
}

export default Home;
